import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Center,
  Flex,
  Separator,
  HStack,
  Spinner,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Button as ButtonComponent, SectionCard, StatusBadge, Tooltip } from '@themes/aegix';
import { MdDownload } from 'react-icons/md';
import { FaRegEdit } from 'react-icons/fa';
import {
  HiInformationCircle,
  HiMapPin,
  HiAcademicCap,
  HiIdentification,
  HiUserGroup,
  HiClipboardDocumentList,
  HiDocumentArrowDown,
  HiPlus,
} from 'react-icons/hi2';
import { DrawerComponent } from '../../ui/drawerComponent';
import { toaster } from '../../ui/toaster';
import {
  useReunificationEvent,
  useActivateForAlert,
  usePauseEvent,
  useResumeEvent,
  useCompleteEvent,
} from '../../../hooks/queries/useReunification';
import { useEndAlert, useAlertHistoryDetail } from '../../../hooks/queries/useAlerts';
import { useFeature } from '../../../hooks/useFeature';
import { useSearchDebounce } from '../../../hooks/useSearchDebounce';
import { FeatureCode } from '../../../types/features';
import { DrawerSideMenu, type DrawerMenuItem } from './DrawerSideMenu';
import { EventDetailOverview } from './EventDetailOverview';
import { EventLocationsPanel } from './EventLocationsPanel';
import { EventStaffPanel } from './EventStaffPanel';
import { EventStudentsPanel } from './EventStudentsPanel';
import { EventGuardiansPanel } from './EventGuardiansPanel';
import { EventAuditLogPanel } from './EventAuditLogPanel';
import { EventReportPanel } from './EventReportPanel';
import { AlertDetailOverview } from './AlertDetailOverview';
import { IncidentDetailOverview } from './IncidentDetailOverview';
import { IncidentEventsTable } from './IncidentEventsTable';
import { AlertHistoryEventsTab } from './AlertHistoryEventsTab';
import { AlertHistoryDeliveryTab } from './AlertHistoryDeliveryTab';
import { AlertHistoryExportDialog } from './AlertHistoryExportDialog';
import { useOrganizations } from '../../../hooks/queries/useOrganizations';
import {
  useAdhocProviderInvitation,
  useCreateAdhocProviderInvitation,
  useDeleteAdhocProviderInvitation,
} from '../../../hooks/queries/useProviderInvitation';
import { PerActiveAlertDrawer } from '../../forms/PerActiveAlertDrawer';
import { useSchoolDetails } from '../../../hooks/queries/useSchool';

type EventTypeBadge = 'alert' | 'reunification' | 'drill' | 'incident';

interface AdhocProviderDisplayItem {
  id: number | string;
  provider_org: number | string;
  provider_org_name: string;
  status: string;
}

interface EventDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  eventType: EventTypeBadge;
  /** True for cancelled or pending-approval alerts/drills — gates the Reunify CTA. Defaults to false. */
  isEnded?: boolean;
  originalAlertId?: number;
}

const DEFAULT_INCIDENT_FILTER = 'incident,chat,report';

export function EventDetailDrawer({
  isOpen,
  onClose,
  eventId,
  eventType,
  isEnded = false,
  originalAlertId,
}: EventDetailDrawerProps) {
  const { t } = useTranslation();
  const menuItems: DrawerMenuItem[] = useMemo(
    () => [
      { value: 'overview', label: t('events.menu.overview', 'Overview'), icon: HiInformationCircle },
      { value: 'locations', label: t('events.menu.locations', 'Locations'), icon: HiMapPin },
      { value: 'staff', label: t('events.menu.staff', 'Staff'), icon: HiAcademicCap },
      { value: 'students', label: t('events.menu.students', 'Students'), icon: HiIdentification },
      { value: 'guardians', label: t('events.menu.guardians', 'Guardians'), icon: HiUserGroup },
      { value: 'report', label: t('events.menu.report', 'Report'), icon: HiDocumentArrowDown },
      { value: 'audit_log', label: t('events.menu.auditLog', 'Audit Log'), icon: HiClipboardDocumentList },
    ],
    [t],
  );
  const [activeSection, setActiveSection] = useState('overview');
  const [isPerActiveAlertDrawerOpen, setIsPerActiveAlertDrawerOpen] = useState(false);

  // State shared between the Events tab and the drawer-header export button
  // for alert/drill events. Lifted here so the header download icon can read
  // the same `incidentTypes` + `query` the table is currently displaying.
  const {
    searchValue,
    setSearchValue,
    debouncedQuery,
  } = useSearchDebounce();
  const [incidentTypes, setIncidentTypes] = useState(DEFAULT_INCIDENT_FILTER);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [eventResultCount, setEventResultCount] = useState(0);

  const isReunificationType = eventType === 'reunification';
  const isAlertOrDrill = eventType === 'alert' || eventType === 'drill';
  const numericEventId = parseInt(eventId, 10);

  // Real-time reunification events flow through the centralized
  // SocketMessageRouter mounted at the app root — no per-event
  // WebSocket needed here.

  const { data: eventDetail, isLoading } = useReunificationEvent(
    isReunificationType ? eventId : '',
  );

  const { data: alertDetail } = useAlertHistoryDetail(
    isAlertOrDrill && !isNaN(numericEventId) ? numericEventId : 0,
  );

  const activeAlertId =
    originalAlertId ?? alertDetail?.original_alert_id ?? (isNaN(numericEventId) ? undefined : numericEventId);
  const siteId =
    alertDetail?.school ||
    (eventDetail?.sites && eventDetail.sites[0]?.site) ||
    undefined;

  const { data: schoolDetail } = useSchoolDetails(siteId ? String(siteId) : '');
  const schoolOrganizationId = schoolDetail?.organization_id ?? undefined;

  const { data: adhocProviderData, isLoading: isLoadingAdhoc } =
    useAdhocProviderInvitation(
      isAlertOrDrill
        ? { alert: activeAlertId }
        : { alert: undefined },
    );

  // GET /api/v2/provider-invitation/ is a paginated collection — never a
  // bare array or a single object — so this needs no runtime shape guessing.
  const adhocList = adhocProviderData?.results ?? [];

  // The "ended" status comes from the events list row (cancelled or
  // pending-approval). The list already has it from the alert-history API,
  // and the singular `/alert-history-detailed/{id}/` retrieve endpoint
  // doesn't reliably return data — relying on it permanently hides the
  // CTA on active alerts. Trusting the row state is both correct and free.
  const isReunificationEnabled = useFeature(FeatureCode.REUNIFICATION);
  const activateForAlertMutation = useActivateForAlert();
  const pauseMutation = usePauseEvent();
  const resumeMutation = useResumeEvent();
  const completeMutation = useCompleteEvent();
  const endAlertMutation = useEndAlert();
  const { data: orgsData } = useOrganizations();

  const createAdhocMutation = useCreateAdhocProviderInvitation();
  const deleteAdhocMutation = useDeleteAdhocProviderInvitation();

  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [reinvitingId, setReinvitingId] = useState<number | null>(null);

  const handleDeleteProviderOrg = async (invitationId: number) => {
    setDeletingId(invitationId);
    try {
      await deleteAdhocMutation.mutateAsync(invitationId);
      toaster.create({
        title: t('providerInvitation.invitationRevokedSuccess', 'Invitation revoked successfully'),
        type: 'success',
      });
    } catch {
      toaster.create({
        title: t('providerInvitation.failedRevokeInvitation', 'Failed to revoke invitation'),
        type: 'error',
      });
    } finally {
      setDeletingId(null);
    }
  };

  // There is no `/reinvite/` action on ProviderInvitationViewSet (ADR-131 D2:
  // revoked is terminal on the row's own state machine). The ordinary create
  // path re-opens a revoked (alert, provider_org) row in place, so reinviting
  // IS creating — same endpoint, same payload.
  const handleReinviteProviderOrg = async (item: AdhocProviderDisplayItem) => {
    if (activeAlertId === undefined) return;
    setReinvitingId(Number(item.id));
    try {
      await createAdhocMutation.mutateAsync({
        alert: activeAlertId,
        provider_org: Number(item.provider_org),
      });
      toaster.create({
        title: t('providerInvitation.invitationSentSuccess', 'Invitation sent successfully'),
        type: 'success',
      });
    } catch (err: unknown) {
      const errObj = err as {
        detail?: string;
        message?: string;
      };
      const rawDetail = errObj?.detail || errObj?.message;
      const detail =
        typeof rawDetail === 'string' && rawDetail.trim()
          ? rawDetail
          : t(
            'providerInvitation.failedReinviteProviderOrg',
            'Failed to reinvite provider organization',
          );

      toaster.create({
        title: detail,
        type: 'error',
      });
    } finally {
      setReinvitingId(null);
    }
  };

  const adhocProviderDisplayItems: AdhocProviderDisplayItem[] = useMemo(() => {
    return adhocList.map((item) => {
      const orgId = item.provider_org || item.id;
      const matchedOrg = (orgsData ?? []).find(
        (o) => o.id === orgId || o.value === String(orgId) || o.publicId === String(orgId),
      );
      return {
        id: item.id || orgId,
        provider_org: orgId,
        provider_org_name: item.provider_org_name || matchedOrg?.label || `Organization #${orgId}`,
        status: item.status || 'invited',
      };
    });
  }, [adhocList, orgsData]);

  const handleStartReunification = () => {
    const numericId = parseInt(eventId, 10);
    if (isNaN(numericId)) return;
    activateForAlertMutation.mutate(numericId, {
      onSuccess: () => {
        toaster.create({
          title: t('events.reunificationStarted', 'Reunification started'),
          description: t('events.reunificationStartedDesc', 'Reunification event has been activated for this alert.'),
          type: 'success',
        });
        handleClose();
      },
      onError: (err) => {
        toaster.create({
          title: t('events.failedStartReunification', 'Failed to start reunification'),
          description: err.message,
          type: 'error',
        });
      },
    });
  };

  const handleEndAlert = () => {
    const numericId = parseInt(eventId, 10);
    if (isNaN(numericId)) return;
    endAlertMutation.mutate(
      { id: numericId, payload: {} },
      {
        onSuccess: () => {
          toaster.create({
            title:
              eventType === 'drill'
                ? t('events.drillEnded', 'Drill ended')
                : t('events.alertEnded', 'Alert ended'),
            type: 'success',
          });
          handleClose();
        },
        onError: (err) => {
          toaster.create({
            title:
              eventType === 'drill'
                ? t('events.failedEndDrill', 'Failed to end drill')
                : t('events.failedEndAlert', 'Failed to end alert'),
            description: err instanceof Error ? err.message : undefined,
            type: 'error',
          });
        },
      },
    );
  };

  const handlePause = () => {
    pauseMutation.mutate(eventId, {
      onSuccess: () => {
        toaster.create({ title: t('events.eventPaused', 'Event paused'), type: 'success' });
      },
      onError: (err) => {
        toaster.create({
          title: t('events.failedPauseEvent', 'Failed to pause event'),
          description: err.message,
          type: 'error',
        });
      },
    });
  };

  const handleResume = () => {
    resumeMutation.mutate(eventId, {
      onSuccess: () => {
        toaster.create({ title: t('events.eventResumed', 'Event resumed'), type: 'success' });
      },
      onError: (err) => {
        toaster.create({
          title: t('events.failedResumeEvent', 'Failed to resume event'),
          description: err.message,
          type: 'error',
        });
      },
    });
  };

  const handleComplete = () => {
    completeMutation.mutate(eventId, {
      onSuccess: () => {
        toaster.create({ title: t('events.eventCompleted', 'Event completed'), type: 'success' });
        onClose();
      },
      onError: (err) => {
        toaster.create({
          title: t('events.failedCompleteEvent', 'Failed to complete event'),
          description: err.message,
          type: 'error',
        });
      },
    });
  };

  const handleClose = () => {
    setActiveSection('overview');
    onClose();
  };

  const drawerTitle = eventDetail
    ? `${eventDetail.school_name} — ${eventDetail.incident_type_display}`
    : isReunificationType
      ? t('events.eventDetail', 'Event Detail')
      : t('events.typeDetail', '{{type}} Detail', { type: eventType.charAt(0).toUpperCase() + eventType.slice(1) });

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={handleClose}
      title={drawerTitle}
      size="xl"
      closeOnOutside={false}
      closeOnEsc={false}
      footer={
        eventDetail && isReunificationType ? (
          <HStack gap={3} width="100%">
            {eventDetail.status === 'active' && (
              <ButtonComponent
                visual="secondary"
                onClick={handlePause}
                disabled={pauseMutation.isPending}
                data-e2e="pause-button">
                {pauseMutation.isPending
                  ? t('events.pausing', 'Pausing...')
                  : t('common.pause', 'Pause')}
              </ButtonComponent>
            )}
            {eventDetail.status === 'paused' && (
              <ButtonComponent
                visual="primary"
                onClick={handleResume}
                disabled={resumeMutation.isPending}
                data-e2e="resume-button">
                {resumeMutation.isPending
                  ? t('events.resuming', 'Resuming...')
                  : t('common.resume', 'Resume')}
              </ButtonComponent>
            )}
            {(eventDetail.status === 'active' ||
              eventDetail.status === 'paused') && (
                <ButtonComponent
                  visual="alert"
                  onClick={handleComplete}
                  disabled={completeMutation.isPending}
                  data-e2e="complete-button">
                  {completeMutation.isPending
                    ? t('events.completing', 'Completing...')
                    : t('common.complete', 'Complete')}
                </ButtonComponent>
              )}
            <ButtonComponent visual="secondary" onClick={handleClose} data-e2e="close-button">
              {t('common.close', 'Close')}
            </ButtonComponent>
          </HStack>
        ) : !isReunificationType ? (
          <HStack gap={3} width="100%">
            {isAlertOrDrill && isReunificationEnabled && !isEnded && (
              <ButtonComponent
                visual="reunification"
                onClick={handleStartReunification}
                disabled={activateForAlertMutation.isPending}
              >
                {activateForAlertMutation.isPending
                  ? t('events.reunifying', 'Reunifying...')
                  : t('events.reunify', 'Reunify')}
              </ButtonComponent>
            )}
            {isAlertOrDrill && !isEnded && (
              <ButtonComponent
                visual="alert"
                onClick={handleEndAlert}
                disabled={endAlertMutation.isPending}
                data-e2e="close-button"
              >
                {endAlertMutation.isPending
                  ? t('events.ending', 'Ending...')
                  : eventType === 'drill'
                    ? t('events.endDrill', 'End Drill')
                    : t('events.endAlert', 'End Alert')}
              </ButtonComponent>
            )}
            <ButtonComponent visual="secondary" onClick={handleClose}>
              {t('common.close', 'Close')}
            </ButtonComponent>
          </HStack>
        ) : undefined
      }
    >
      {isAlertOrDrill ? (
        <Flex direction="column" gap={4} py={2} minH={0} flex={1}>
          {/* Drawer header row: download trigger conditional on results */}
          {eventResultCount > 0 && (
            <Flex justify="flex-end">
              <ButtonComponent
                visual="primary"
                onClick={() => setExportDialogOpen(true)}
                aria-label={t('events.exportEventDetail', 'Export event detail')}
                data-e2e="export-button"
              >
                <MdDownload />
              </ButtonComponent>
            </Flex>
          )}

          <AlertDetailOverview alertId={numericEventId} />

          {!isEnded && (
            <SectionCard
              title={t('sites.perSite', 'Per Site')}
              action={(() => {
                const hasProviderOrgs = adhocProviderDisplayItems.length > 0;
                const label = hasProviderOrgs
                  ? t('sites.editProviderOrgs', 'Edit provider organizations')
                  : t('sites.addProviderOrgs', 'Add provider organizations');
                return (
                  <Tooltip content={label}>
                    <ButtonComponent
                      visual="secondary"
                      size="sm"
                      type="button"
                      aria-label={label}
                      onClick={() => setIsPerActiveAlertDrawerOpen(true)}
                      data-e2e="add-per-site-provider-button"
                    >
                      {hasProviderOrgs ? (
                        <FaRegEdit size={14} />
                      ) : (
                        <HiPlus size={14} />
                      )}
                    </ButtonComponent>
                  </Tooltip>
                );
              })()}
            >
              {isLoadingAdhoc ? (
                <Flex align="center" gap={2} py={1}>
                  <Spinner size="xs" color="button.primary" />
                  <Text fontSize="xs" color="text.secondary">
                    {t('common.loading', 'Loading...')}
                  </Text>
                </Flex>
              ) : adhocProviderDisplayItems.length > 0 ? (
                <VStack gap={2} align="stretch">
                  {adhocProviderDisplayItems.map((item: AdhocProviderDisplayItem) => (
                    <Flex
                      key={item.id}
                      justify="space-between"
                      align="center"
                      py={1.5}
                      px={3}
                      bg="surface.secondary"
                      border="1px solid"
                      borderColor="border.secondary"
                      borderRadius="sm"
                      data-e2e={`preselected-provider-item-${item.id}`}
                    >
                      <Text fontSize="sm" fontWeight={500} color="text.primary">
                        {item.provider_org_name}
                      </Text>
                      <HStack gap={2} align="center">
                        <StatusBadge
                          status={item.status === 'invited' ? 'active' : 'inactive'}
                          label={
                            item.status === 'invited'
                              ? t('providerInvitation.invited', 'Invited')
                              : item.status === 'revoked'
                                ? t('providerInvitation.revoked', 'Revoked')
                                : String(item.status)
                          }
                        />
                        {item.status === 'invited' && (
                          <ButtonComponent
                            visual="secondary"
                            aria-label={t(
                              'providerInvitation.revokeInvitationAriaLabel',
                              'Revoke provider organization invitation',
                            )}
                            onClick={() => handleDeleteProviderOrg(Number(item.id))}
                            disabled={deletingId === Number(item.id)}
                            data-e2e={`revoke-provider-org-${item.id}`}
                          >
                            {deletingId === Number(item.id) ? (
                              <Spinner size="xs" />
                            ) : (
                              t('providerInvitation.revoke', 'Revoke')
                            )}
                          </ButtonComponent>
                        )}
                        {item.status === 'revoked' && (
                          <ButtonComponent
                            visual="secondary"
                            aria-label={t(
                              'providerInvitation.reinviteAriaLabel',
                              'Reinvite provider organization',
                            )}
                            onClick={() => handleReinviteProviderOrg(item)}
                            disabled={reinvitingId === Number(item.id)}
                            data-e2e={`reinvite-provider-org-${item.id}`}
                          >
                            {reinvitingId === Number(item.id) ? (
                              <Spinner size="xs" />
                            ) : (
                              t('providerInvitation.reinvite', 'Reinvite')
                            )}
                          </ButtonComponent>
                        )}
                      </HStack>
                    </Flex>
                  ))}
                </VStack>
              ) : (
                <Text fontSize="xs" color="text.secondary">
                  {t('providerInvitation.noProviderOrgsConfigured', 'No provider organizations configured.')}
                </Text>
              )}
            </SectionCard>
          )}

          <Separator />

          <Tabs.Root
            defaultValue="events"
            display="flex"
            flexDirection="column"
            flex={1}
            minH={0}
          >
            <Tabs.List>
              <Tabs.Trigger value="events">{t('events.tab.events', 'Events')}</Tabs.Trigger>
              <Tabs.Trigger value="delivery">{t('events.tab.delivery', 'Delivery')}</Tabs.Trigger>
            </Tabs.List>

            <Tabs.Content
              value="events"
              flex={1}
              display="flex"
              flexDirection="column"
              minH={0}
            >
              <AlertHistoryEventsTab
                alertId={numericEventId}
                searchValue={searchValue}
                onSearchValueChange={setSearchValue}
                debouncedQuery={debouncedQuery}
                incidentTypes={incidentTypes}
                onIncidentTypesChange={(csv) =>
                  setIncidentTypes(csv || DEFAULT_INCIDENT_FILTER)
                }
                onResultCountChange={setEventResultCount}
              />
            </Tabs.Content>

            <Tabs.Content value="delivery" pt={3}>
              <AlertHistoryDeliveryTab alertId={numericEventId} />
            </Tabs.Content>
          </Tabs.Root>

          <AlertHistoryExportDialog
            isOpen={exportDialogOpen}
            onClose={() => setExportDialogOpen(false)}
            payload={{
              id: numericEventId,
              incident_types: incidentTypes,
              query: debouncedQuery,
            }}
          />
        </Flex>
      ) : eventType === 'incident' ? (
        <VStack align="stretch" gap={4} py={2}>
          <IncidentDetailOverview incidentId={numericEventId} />
          <IncidentEventsTable incidentId={numericEventId} />
        </VStack>
      ) : isLoading || !eventDetail ? (
        <Center py={12}>
          <Spinner size="lg" color="button.primary" />
        </Center>
      ) : (
        <HStack align="start" gap={0} minH="400px">
          <Box
            w="180px"
            minW="180px"
            borderRight="1px solid"
            borderColor="border.subtle"
            pr={3}
          >
            <DrawerSideMenu
              activeItem={activeSection}
              onItemChange={setActiveSection}
              items={menuItems}
            />
          </Box>
          <Box flex={1} pl={4} overflow="auto">
            {activeSection === 'overview' && (
              <EventDetailOverview event={eventDetail} />
            )}
            {activeSection === 'locations' && (
              <EventLocationsPanel
                eventId={eventId}
                sites={eventDetail.sites ?? []}
              />
            )}
            {activeSection === 'staff' && (
              <EventStaffPanel eventId={eventId} />
            )}
            {activeSection === 'students' && (
              <EventStudentsPanel eventId={eventId} />
            )}
            {activeSection === 'guardians' && (
              <EventGuardiansPanel eventId={eventId} />
            )}
            {activeSection === 'report' && (
              <EventReportPanel eventId={eventId} />
            )}
            {activeSection === 'audit_log' && (
              <EventAuditLogPanel eventId={eventId} />
            )}
          </Box>
        </HStack>
      )}

      {isAlertOrDrill && !isEnded && (
        <PerActiveAlertDrawer
          isOpen={isPerActiveAlertDrawerOpen}
          onClose={() => setIsPerActiveAlertDrawerOpen(false)}
          alertId={activeAlertId}
          excludeOrganizationId={schoolOrganizationId}
        />
      )}
    </DrawerComponent>
  );
}
